<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-flex>
        <dt-search-box @typing="searchValue" @reset-datatable="resetDataTable()" @clear-search-field="search = null" />
      </v-flex>
    </v-card>

    <v-card flat>
      <v-layout row>
        <v-layout row wrap justify-start align-end>
          <v-flex pa-2>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  style="margin:0 10px 0; height: 30px; width: 30px;"
                  :loading="loadThePrintBtn"
                  flat
                  fab
                  v-on="on"
                  color="primary"
                  @click="getNoPaginatedData('print')"
                >
                  <v-icon>print</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(`Print Table`) }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  style="margin: 0; height: 30px; width: 30px"
                  fab
                  :loading="loadTheExportBtn"
                  flat
                  v-on="on"
                  color="primary"
                  @click="getNoPaginatedData('export')"
                >
                  <v-icon>fa-file-excel</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(`Export Table`) }}</span>
            </v-tooltip>
          </v-flex>
        </v-layout>

        <v-layout row justify-end>
          <v-flex shrink pa-2>
            <v-menu v-model="startDateMenu" full-width max-width="290" :close-on-content-click="false">
              <v-text-field
                slot="activator"
                :value="startDate"
                :label="$t('Start Date')"
                prepend-inner-icon="event"
                hide-details
                readonly
                name="start_date"
                v-validate="'date_format:yyyy-MM-dd'"
                :error="errors.has('start_date')"
              />
              <v-date-picker @change="startDateMenu = false" v-model="startDate" @input="updateDatatableResult()" />
            </v-menu>
          </v-flex>

          <v-flex shrink pa-2>
            <v-menu v-model="endDateMenu" full-width max-width="290" :close-on-content-click="false">
              <v-text-field
                slot="activator"
                :value="endDate"
                :label="$t('End Date')"
                prepend-inner-icon="event"
                hide-details
                readonly
                name="end_date"
                v-validate="'date_format:yyyy-MM-dd'"
                :error="errors.has('end_date')"
              />
              <v-date-picker @change="endDateMenu = false" v-model="endDate" @input="updateDatatableResult()" />
            </v-menu>
          </v-flex>
        </v-layout>
      </v-layout>
    </v-card>

    <v-card px-4 pb-4>
      <v-data-table
        :headers="headers"
        :items="resultData.data"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="pagination.rowsPerPageItems"
        :total-items="resultData.total"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.general_journal_id }}</td>-->
          <td>{{ props.item.journal_code }}</td>
          <td>{{ props.item.credit_account_name }}</td>
          <td>{{ props.item.debit_account_name }}</td>
          <td>{{ props.item.journal_date }}</td>
          <td>{{ props.item.debit }}</td>
          <td>{{ props.item.credit }}</td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
          $t('Sorry, No results found for your search criteria')
        }}</v-alert>
      </v-data-table>

      <dt-pagination
        :resultData="resultData"
        :pagination="pagination"
        :perPageNumber="perPageNumber"
        @page-changed="pageChanged"
        @per-page-changed="resultsPerPageChanged"
      />

      <print-export-component
        :printExportDialog="dialogs.printExportDialog"
        :tableHeader="tableHeader"
        :tableBody="tableBody"
        :clickedButton="clickedButton"
        @hidemodal="dialogs.printExportDialog = false"
      />
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import dtSearchBox from '@/views/_partials/AppSearchBox.vue'
import dtPagination from '@/views/_partials/AppDatatablePagination.vue'
import Axios from 'axios'
import printExportComponent from '@/views/_partials/printExportComponent.vue'

export default {
  components: {
    dtSearchBox,
    dtPagination,
    printExportComponent
  },

  data() {
    return {
      dialogs: {
        printExportDialog: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      startDateMenu: false,
      startDate: this.$moment()
        .format()
        .substr(0, 10),
      endDateMenu: false,
      endDate: this.$moment()
        .format()
        .substr(0, 10),
      id: Number(this.$route.query.id),
      name: this.$route.query.name,
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'general_journal_id',
      perPageNumber: 10,
      pagination: {
        sortBy: this.defaultSortBy,
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 1
      },
      oldPagination: {},
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'general_journal_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Journal Code'), value: 'journal_code' },
        { text: this.$t('Credit Account'), value: 'credit_account_name' },
        { text: this.$t('Debit Account'), value: 'debit_account_name' },
        { text: this.$t('Date'), value: 'journal_date' },
        { text: this.$t('Credit Amount'), value: 'credit' },
        { text: this.$t('Debit Amount'), value: 'debit' }
      ],
      allowGettingData: true
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: `${this.$t('Cashier Debit & Credit Transactions')}: <strong class="title-highlight">${this.name}</strong>`,
      icon: 'attach_money'
    })
  },

  computed: {
    ...mapGetters({
      resultData: 'cashierDebitCredit/getDebitCreditList'
    })
  },

  watch: {
    pagination: {
      immediate: true,
      handler() {
        let p = this.pagination
        let op = this.oldPagination
        if (p.sortBy !== op.sortBy || p.descending !== op.descending) {
          op.sortBy = p.sortBy
          op.descending = p.descending
          p.page = 1
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        } else if (this.allowGettingData || this.search !== this.oldSearch) {
          this.oldSearch = this.search
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        }
      }
    }
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'cashierDebitCredit/updateDebitCreditList',
      fetchInfo: 'cashierDebitCredit/fetchDebitCreditInfo'
    }),

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get(`cashier_debit_credit_index/${this.id}`, {
        params: {
          is_paginated: 0,
          search: this.search,
          start_date: this.startDate,
          end_date: this.endDate
        }
      })
        .then(response => {
          this.tableBody = response.data.data
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    updateDatatableResult() {
      this.loading = true
      let payload = {
        ...this.pagination,
        search: this.search,
        start_date: this.startDate,
        end_date: this.endDate,
        id: this.id
      }
      this.updateResult(payload)
        .then(() => {
          this.pagination.page = this.resultData.current_page
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    resetDataTable() {
      this.allowGettingData = true
      this.pagination = {
        sortBy: null,
        descending: true,
        page: 1,
        rowsPerPage: this.perPageNumber,
        totalItems: 1
      }
      this.search = this.oldSearch = null
    },

    resultsPerPageChanged(val) {
      this.perPageNumber = val
      this.pagination.rowsPerPage = val
      this.pagination.page = 1
      this.updateDatatableResult()
    },

    pageChanged(val) {
      this.pagination.page = val
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
    }
  }
}
</script>

<style scoped></style>
